/**
 * Khartiia Rechova: small site-wide behaviours that do not belong to a single section.
 */
import { lockScroll, unlockScroll } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';

// Product card quick add (design/updates/card-quickadd, markup in kh-product-card). Mouse only — touch has no «+».
// Hover on «+» unfolds the sizes row, leaving it folds back after 200 ms; click/Enter on «+» opens it and focuses the
// first available size; ← → move between sizes, Esc folds and returns to «+». A size (or «Додати в кошик») goes to
// the cart: «✓ Додано · M», cart count +1 right away, the cart drawer after 1 s, then a Mint «✓» on the card.
// A struck-through size opens the product with that size. Other «+» hand the click to Horizon's quick add modal.
const QA_CLOSE_MS = 200;
const QA_DRAWER_MS = 1000;
/** @type {WeakMap<HTMLElement, number>} */
const qaTimers = new WeakMap();

/** @param {HTMLElement} qa */
function qaOpen(qa) {
  window.clearTimeout(qaTimers.get(qa));
  if (qa.classList.contains('is-open') || qa.classList.contains('is-done')) return;
  document.querySelectorAll('[data-kh-qa].is-open').forEach((other) => other !== qa && qaClose(/** @type {HTMLElement} */ (other), 0));
  qa.classList.add('is-open');
  qa.querySelector('[data-kh-qa-toggle]')?.setAttribute('aria-expanded', 'true');
}

/** @param {HTMLElement} qa @param {number} [delay] */
function qaClose(qa, delay = QA_CLOSE_MS) {
  window.clearTimeout(qaTimers.get(qa));
  if (qa.classList.contains('is-busy')) return; // «Додано» is showing: nothing to fold
  const close = () => {
    qa.classList.remove('is-open');
    qa.querySelector('[data-kh-qa-toggle]')?.setAttribute('aria-expanded', 'false');
  };
  if (delay) qaTimers.set(qa, window.setTimeout(close, delay));
  else close();
}

/** @param {EventTarget | null} node */
const qaPart = (node) =>
  node instanceof Element ? /** @type {HTMLElement | null} */ (node.closest('.kh-qa__plus, .kh-qa__row')) : null;

document.addEventListener('pointerover', (event) => {
  if (event.pointerType !== 'mouse') return;
  const part = qaPart(event.target);
  const qa = /** @type {HTMLElement | null} */ (part?.closest('[data-kh-qa]'));
  if (part && qa && !part.contains(/** @type {Node | null} */ (event.relatedTarget))) qaOpen(qa);
});

document.addEventListener('pointerout', (event) => {
  if (event.pointerType !== 'mouse') return;
  const from = /** @type {Element} */ (event.target);
  const to = /** @type {Node | null} */ (event.relatedTarget);
  const card = from.closest?.('.kh-card');
  const qa = /** @type {HTMLElement | null} */ (card?.querySelector('[data-kh-qa].is-open') ?? null);
  if (!card || !qa) return;
  if (!to || !card.contains(to)) {
    qaClose(qa, 0);
    return;
  }
  const part = qaPart(from);
  if (part && !part.contains(to) && !qaPart(to)) qaClose(qa);
});

document.addEventListener('focusout', (event) => {
  const qa = /** @type {HTMLElement | null} */ (
    event.target instanceof Element ? event.target.closest('[data-kh-qa].is-open') : null
  );
  if (qa && !qa.contains(/** @type {Node | null} */ (event.relatedTarget))) qaClose(qa, 0);
});

document.addEventListener('keydown', (event) => {
  const target = /** @type {HTMLElement} */ (event.target);
  const qa = /** @type {HTMLElement | null} */ (target.closest?.('[data-kh-qa]'));
  if (!qa) return;
  if (event.key === 'Escape' && qa.classList.contains('is-open')) {
    event.preventDefault();
    qaClose(qa, 0);
    /** @type {HTMLElement | null} */ (qa.querySelector('[data-kh-qa-toggle]'))?.focus();
    return;
  }
  if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
  const chips = /** @type {HTMLElement[]} */ ([...qa.querySelectorAll('.kh-qa__chip')]);
  const index = chips.indexOf(target);
  if (index < 0) return;
  event.preventDefault();
  chips[(index + (event.key === 'ArrowRight' ? 1 : chips.length - 1)) % chips.length]?.focus();
});

document.addEventListener('click', (event) => {
  const target = /** @type {HTMLElement} */ (event.target);
  const chip = /** @type {HTMLElement | null} */ (target.closest?.('.kh-qa__chip'));
  if (chip?.getAttribute('aria-disabled') === 'true') {
    event.preventDefault();
    if (chip.dataset.khQaHref) window.location.href = chip.dataset.khQaHref;
    return;
  }
  const add = /** @type {HTMLElement | null} */ (target.closest?.('[data-kh-qa-add]'));
  if (add) {
    event.preventDefault();
    quickAddToCart(add);
    return;
  }
  const toggle = /** @type {HTMLElement | null} */ (target.closest?.('[data-kh-qa-toggle]'));
  const qa = /** @type {HTMLElement | null} */ (toggle?.closest('[data-kh-qa]') ?? null);
  if (toggle && qa) {
    event.preventDefault();
    qaOpen(qa);
    /** @type {HTMLElement | null} */ (qa.querySelector('.kh-qa__chip:not([aria-disabled])') ?? qa.querySelector('.kh-qa__chip'))?.focus();
    return;
  }
  if (!target.closest?.('[data-kh-qa]')) {
    document.querySelectorAll('[data-kh-qa].is-open').forEach((open) => qaClose(/** @type {HTMLElement} */ (open), 0));
  }
  const button = target.closest?.('.kh-card__quick-btn');
  if (!button) return;
  const quickAdd = /** @type {HTMLElement & { handleClick?: (event: Event) => void } | null} */ (
    button.closest('quick-add-component')
  );
  if (typeof quickAdd?.handleClick !== 'function') return;
  event.preventDefault();
  quickAdd.handleClick(event);
});

/** @param {HTMLElement} button */
async function quickAddToCart(button) {
  const qa = /** @type {HTMLElement | null} */ (button.closest('[data-kh-qa]'));
  if (!qa || qa.classList.contains('is-busy')) return;
  const card = qa.closest('.kh-card');
  const variantId = button.dataset.khQaAdd;
  const label = button.dataset.khQaLabel;
  const clickedAt = Date.now();

  // Optimistic: «✓ Додано · M» right away (the request takes 0.3–1.5 s); the row stays put while it runs and
  // goes back to «+» with an error toast if the add fails.
  window.clearTimeout(qaTimers.get(qa));
  qa.classList.add('is-busy');
  const text = qa.querySelector('[data-kh-qa-done-text]');
  if (text) text.textContent = label ? `${khI18n().added} · ${label}` : khI18n().added;
  qa.classList.add('is-done');
  qa.classList.remove('is-open');
  qa.querySelector('[data-kh-qa-toggle]')?.setAttribute('aria-expanded', 'false');

  const deferred = CartLinesUpdateEvent.createPromise();
  // Horizon's drawer opens itself on every add; the design opens it 1 s after «✓ Додано», so it skips this one.
  const drawerComponent = document.querySelector('cart-drawer-component[auto-open]');
  drawerComponent?.removeAttribute('auto-open');
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: `gid://shopify/ProductVariant/${variantId}`, quantity: 1 }],
      promise: deferred.promise,
    })
  );
  drawerComponent?.setAttribute('auto-open', '');
  try {
    const root = window.Shopify?.routes?.root || '/';
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
      .filter(Boolean);
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }], sections: sectionIds.join(',') }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || 'add failed');
    const cart = await fetch(`${root}cart.js`).then((r) => r.json());
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: data.items, itemCount: 1, sections: data.sections, source: 'kh-card-quick-add' },
    });
    // The drawer opens 1 s after the click, or as soon as the cart is updated if that took longer
    window.setTimeout(() => {
      const drawer = /** @type {HTMLElement & { open?: () => void } | null} */ (document.getElementById('cart-drawer'));
      drawer?.open?.();
      // Swap «Додано» for the Mint «✓» once the drawer covers the card
      window.setTimeout(() => {
        qa.classList.remove('is-done', 'is-busy');
        card?.classList.add('is-in-cart');
      }, 240);
    }, Math.max(0, QA_DRAWER_MS - (Date.now() - clickedAt)));
  } catch (error) {
    deferred.reject(error);
    qa.classList.remove('is-done', 'is-busy');
    window.khToast?.({ type: 'error', title: khI18n().addFailed, text: error instanceof Error ? error.message : '' });
  }
}

// Mint «✓» on cards whose product is in the cart: Liquid sets it on render; refresh after the cart drawer closes
// (lines may have been removed there or added through Horizon's quick add modal).
document.addEventListener('theme-drawer:close', async (event) => {
  if (/** @type {HTMLElement} */ (event.target)?.id !== 'cart-drawer') return;
  try {
    const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((r) => r.json());
    const ids = new Set((cart.items || []).map((/** @type {{ product_id: number }} */ item) => String(item.product_id)));
    document.querySelectorAll('.kh-card[data-product-id]').forEach((el) => {
      el.classList.toggle('is-in-cart', ids.has(/** @type {HTMLElement} */ (el).dataset.productId || ''));
    });
  } catch {
    // Keeps the marks from the page render
  }
});

// Drawers / sheets built on <dialog data-kh-dialog>: open by [data-kh-dialog-open="id"],
// close by [data-kh-dialog-close], Esc (native) or a click on the backdrop.
/** @type {WeakMap<HTMLDialogElement, HTMLElement>} */
const dialogOpeners = new WeakMap();

document.addEventListener('click', (event) => {
  const target = /** @type {HTMLElement} */ (event.target);
  const opener = target.closest?.('[data-kh-dialog-open]');
  if (opener instanceof HTMLElement) {
    const dialog = document.getElementById(opener.dataset.khDialogOpen || '');
    if (dialog instanceof HTMLDialogElement && !dialog.open) {
      event.preventDefault();
      dialogOpeners.set(dialog, opener);
      dialog.showModal();
      lockScroll(dialog);
    }
    return;
  }
  const dialog = target.closest?.('dialog[data-kh-dialog]');
  if (!(dialog instanceof HTMLDialogElement)) return;
  if (target.closest('[data-kh-dialog-close]') || target === dialog) closeKhDialog(dialog);
});

/**
 * Closes a kh dialog; a side panel (.kh-side-panel) slides out first, like the cart (design/updates/cart-panels); on a
 * phone it and the discount modal (.kh-modal) are bottom sheets and sink down (kh-base.css, kh-sheet-out); on desktop
 * the discount modal rises back and fades (kh-drop-out, 09.10).
 * @param {HTMLDialogElement} dialog
 */
function closeKhDialog(dialog) {
  if (!dialog.open || dialog.classList.contains('is-closing')) return;
  const animated =
    dialog.matches('.kh-side-panel, .kh-modal') && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!animated) {
    dialog.close();
    return;
  }
  dialog.classList.add('is-closing');
  const done = () => {
    if (!dialog.classList.contains('is-closing')) return;
    dialog.classList.remove('is-closing');
    dialog.close();
  };
  dialog.addEventListener('animationend', (event) => event.target === dialog && done(), { once: true });
  window.setTimeout(done, 420); // hidden tab: no animationend
}
window.khCloseDialog = closeKhDialog;

// Esc on a side panel: animate out instead of the native instant close
document.addEventListener(
  'cancel',
  (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement) || !dialog.matches('.kh-side-panel, .kh-modal')) return;
    event.preventDefault();
    closeKhDialog(dialog);
  },
  true
);

document.addEventListener(
  'close',
  (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement) || !dialog.hasAttribute('data-kh-dialog')) return;
    unlockScroll(dialog);
    dialogOpeners.get(dialog)?.focus({ preventScroll: true });
  },
  true
);

// Typography for every text the theme does not write itself (page bodies from Shopify, translations, settings,
// product descriptions): a word of one or two letters (і, в, з, до, не, ту…) sticks to the next word, and a dash sticks
// to the word before it, so neither ends / starts a line. Runs on load and on anything Horizon renders later (cart,
// filters, sections); text fields, code and Slice Mono are left alone.
// No lone word on the last line: CSS text-wrap: pretty does it only where supported (not in iOS Safari before 26), so
// the last two words of every text block stick together here (08.10: «…з базою підписників / Вартових.»).
const RAG_SKIP = 'script, style, textarea, input, select, option, code, pre, svg, [contenteditable], .kh-mono';
const RAG_SHORT = /(?<=^|[\s(«„"\u00a0])([A-Za-zА-ЯІЇЄҐа-яіїєґ’']{1,2}) (?=\S)/g;
const RAG_DASH = / ([—–])(?= )/g;
// The corps' name never splits: «НГУ» stays with «Хартія»
const RAG_NAME = /НГУ «/g;
// The last regular space of a block (the glued tail stays short so it never overflows a narrow card)
const RAG_WIDOW = / (?=[^ ]{1,22}\s*$)/;

/** Is this the last bit of text in its element, in an element long enough to wrap (4+ words)? @param {Text} node */
function endsBlock(node) {
  for (let next = node.nextSibling; next; next = next.nextSibling) {
    if (/\S/.test(next.textContent ?? '')) return false;
  }
  const words = (node.parentElement?.textContent ?? '').trim().split(/\s+/).length;
  return words >= 4;
}
/** @param {Node} root */
function ragText(root) {
  if (!(root instanceof Element)) return;
  if (root instanceof Element && root.closest(RAG_SKIP)) return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) =>
      node.nodeValue && /[ ]/.test(node.nodeValue) && !node.parentElement?.closest(RAG_SKIP)
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_REJECT,
  });
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const text = /** @type {string} */ (node.nodeValue);
    let next = text.replace(RAG_SHORT, '$1\u00a0').replace(RAG_DASH, '\u00a0$1').replace(RAG_NAME, 'НГУ\u00a0«');
    if (endsBlock(/** @type {Text} */ (node))) next = next.replace(RAG_WIDOW, '\u00a0');
    if (next !== text) node.nodeValue = next;
  }
}
ragText(document.body);
new MutationObserver((records) => {
  for (const record of records) record.addedNodes.forEach((node) => ragText(node));
}).observe(document.body, { childList: true, subtree: true });

// Focus rings are for keyboard users. Only navigation keys (Tab, arrows, Enter, Space, Home/End, Page Up/Down) count
// as keyboard use; Esc, modifiers and shortcuts (⌘⇧4 for a screenshot, ⌘C…) do not. For a mouse / touch user the
// browser may still draw a ring on the last clicked control — after a dialog closes (focus goes back to its opener),
// after a shortcut, or on coming back to the tab — and we drop it. Text fields keep their focus (the caret is there).
const NAV_KEYS = new Set(['Tab', 'Enter', ' ', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown']);
let lastInput = 'pointer';
const dropPointerRing = () => {
  if (lastInput !== 'pointer') return;
  requestAnimationFrame(() => {
    const active = document.activeElement;
    if (!(active instanceof HTMLElement) || active === document.body) return;
    if (active.matches('input:not([type=button], [type=submit], [type=radio], [type=checkbox]), textarea, select, [contenteditable]')) return;
    if (active.matches(':focus-visible')) active.blur();
  });
};
document.addEventListener('pointerdown', () => (lastInput = 'pointer'), true);
document.addEventListener(
  'keydown',
  (event) => {
    if (NAV_KEYS.has(event.key) && !event.metaKey && !event.ctrlKey && !event.altKey) lastInput = 'keyboard';
    else dropPointerRing();
  },
  true
);
document.addEventListener('keyup', dropPointerRing, true);
window.addEventListener('focus', dropPointerRing);
document.addEventListener('visibilitychange', () => document.visibilityState === 'visible' && dropPointerRing());
document.addEventListener(
  'close',
  (event) => {
    // After the dialog's own focus restore (native and ours above)
    if (event.target instanceof HTMLDialogElement) dropPointerRing();
  },
  true
);

// Product rails: [data-kh-rail] with a scrolling [data-kh-rail-track], prev/next buttons and a scrollbar thumb.
function updateRail(rail) {
  const track = rail.querySelector('[data-kh-rail-track]');
  const thumb = /** @type {HTMLElement | null} */ (rail.querySelector('[data-kh-rail-thumb]'));
  if (!track) return;
  const max = track.scrollWidth - track.clientWidth;
  rail.querySelectorAll('[data-kh-rail-prev]').forEach((b) => (/** @type {HTMLButtonElement} */ (b).disabled = track.scrollLeft <= 1));
  rail.querySelectorAll('[data-kh-rail-next]').forEach((b) => (/** @type {HTMLButtonElement} */ (b).disabled = track.scrollLeft >= max - 1));
  if (thumb) {
    const share = track.scrollWidth ? track.clientWidth / track.scrollWidth : 1;
    thumb.style.width = `${Math.min(100, share * 100)}%`;
    thumb.style.transform = `translateX(${max > 0 ? (track.scrollLeft / max) * ((1 - share) / share) * 100 : 0}%)`;
    thumb.parentElement?.toggleAttribute('hidden', share >= 1);
  }
}

document.addEventListener('click', (event) => {
  const button = /** @type {HTMLElement} */ (event.target).closest?.('[data-kh-rail-prev],[data-kh-rail-next]');
  if (!button) return;
  const track = button.closest('[data-kh-rail]')?.querySelector('[data-kh-rail-track]');
  if (!track) return;
  const step = track.clientWidth * 0.9 * (button.hasAttribute('data-kh-rail-prev') ? -1 : 1);
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  track.scrollBy({ left: step, behavior: reduce ? 'auto' : 'smooth' });
});

document.addEventListener(
  'scroll',
  (event) => {
    const track = event.target;
    if (track instanceof HTMLElement && track.matches('[data-kh-rail-track]')) {
      const rail = track.closest('[data-kh-rail]');
      if (rail) updateRail(rail);
    }
  },
  true
);

// A rail rendered hidden (the empty cart's picks in a closed drawer) has no width yet: its buttons are worked out again
// once the track gets a size (09.10). observe() of an already observed track is a no-op.
const railSizes = new ResizeObserver((entries) => {
  entries.forEach((entry) => {
    const rail = entry.target.closest('[data-kh-rail]');
    if (rail) updateRail(rail);
  });
});
function initRails(root = document) {
  root.querySelectorAll('[data-kh-rail]').forEach((rail) => {
    updateRail(rail);
    const track = rail.querySelector('[data-kh-rail-track]');
    if (track) railSizes.observe(track);
  });
}
initRails();
window.addEventListener('resize', () => initRails());
new MutationObserver((records) => {
  for (const record of records) {
    record.addedNodes.forEach((node) => {
      if (node instanceof HTMLElement && (node.matches('[data-kh-rail]') || node.querySelector('[data-kh-rail]'))) initRails(node.parentElement || document);
    });
  }
}).observe(document.body, { childList: true, subtree: true });

// QR at a Hub. Walnut reads ?loc=<Shopify location ID> (on any page, stored for the session) and limits
// checkout to pickup at that location. We accept the same ID or a short code (?loc=kyiv) and, when not
// already there, open that Hub's collection while keeping ?loc=<location ID> in the URL for Walnut.
(() => {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('loc');
  if (!code) return;
  let hubs = {};
  try {
    hubs = JSON.parse(document.getElementById('kh-hubs')?.textContent || '{}');
  } catch {
    return;
  }
  const hub = hubs[code.toLowerCase()] || hubs[code];
  if (!hub?.url) return;
  const loc = hub.locationId || code;
  if (window.location.pathname === hub.url && code === loc) return;
  const target = new URL(hub.url, window.location.origin);
  target.searchParams.set('loc', loc);
  window.location.replace(target.toString());
})();

// Product recommendations («Схожі»): our own cards come with the page (sections/kh-related.liquid); when there are fewer
// than the limit, Shopify's «related» fill the rest — minus cards already shown and the product's other colours.
class KhRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.dataset.loaded) return;
    if (this.#shown().length >= Number(this.dataset.limit || 8)) return;
    // Cheap request; an empty element has no height, so a visibility observer is unreliable here.
    const start = () => this.load();
    if ('requestIdleCallback' in window) window.requestIdleCallback(start, { timeout: 2000 });
    else window.setTimeout(start, 300);
  }

  /** @returns {HTMLElement[]} */
  #shown() {
    return [...this.querySelectorAll('[data-kh-rail-track] > [data-kh-rec]')];
  }

  async load() {
    const { url, productId, sectionId, intent, group } = this.dataset;
    if (!url || !productId || !sectionId) return;
    this.dataset.loaded = 'true';
    try {
      const response = await fetch(`${url}&product_id=${productId}&section_id=${sectionId}&intent=${intent || 'related'}`);
      if (!response.ok) throw new Error(`Recommendations failed: ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const track = this.querySelector('[data-kh-rail-track]');
      const rail = this.querySelector('[data-kh-rail]');
      if (!track || !rail) return;
      const limit = Number(this.dataset.limit || 8);
      const seen = new Set(this.#shown().map((li) => li.dataset.khRec));
      for (const li of html.querySelectorAll('[data-kh-rail-track] > [data-kh-rec]')) {
        if (this.#shown().length >= limit) break;
        const item = /** @type {HTMLElement} */ (li);
        if (seen.has(item.dataset.khRec) || item.dataset.khRec === productId) continue;
        if (group && item.dataset.khGroup === group) continue;
        seen.add(item.dataset.khRec);
        track.append(document.importNode(item, true));
      }
      if (this.#shown().length) rail.hidden = false;
    } catch (error) {
      console.error(error);
    }
  }
}
if (!customElements.get('kh-recommendations')) customElements.define('kh-recommendations', KhRecommendations);

// «Знижка для своїх»: any [data-kh-discount-open] or the kh:discount-open event opens the dialog.
function openDiscount() {
  const dialog = document.getElementById('KhDiscount');
  if (!(dialog instanceof HTMLDialogElement) || dialog.open) return;
  const cart = /** @type {HTMLElement & { close?: () => void } | null} */ (document.getElementById('cart-drawer'));
  if (cart?.querySelector('dialog[open]')) cart.close?.();
  // Opened from the phone menu (a non-modal dialog under the header): the menu goes, the modal takes its place
  const menu = document.getElementById('KhMenu');
  if (menu instanceof HTMLDialogElement && menu.open) {
    menu.classList.remove('is-closing');
    menu.close();
  }
  dialog.showModal();
  lockScroll(dialog);
}
document.addEventListener('kh:discount-open', openDiscount);
document.addEventListener('click', (event) => {
  // Menu links can't carry data attributes, so a link to #znyzhka (e.g. «Знижка до 20%» in the footer menu) opens it too.
  if (/** @type {HTMLElement} */ (event.target).closest?.('[data-kh-discount-open], a[href$="#znyzhka"]')) {
    event.preventDefault();
    openDiscount();
  }
});

/* ---------- System states (design/boards V1-SysDetails) ---------- */

/** @returns {Record<string, string>} */
function khI18n() {
  try {
    return JSON.parse(document.getElementById('kh-i18n')?.textContent || '{}');
  } catch {
    return {};
  }
}

/**
 * One toast at a time. success = Mint, undo = White with outline and an action, error = Ink.
 * 4 s, 6 s with an action; paused while hovered or focused.
 * @param {{ type?: 'success' | 'undo' | 'error', title: string, text?: string, action?: string, onAction?: () => void }} options
 */
function khToast({ type = 'success', title, text = '', action = '', onAction }) {
  const baseHost = document.querySelector('[data-kh-toasts]');
  if (!baseHost) return;
  // A modal dialog (cart on narrow screens, discount modal) sits in the top layer above any z-index,
  // so the toast has to live inside it while it is open.
  const modal = [...document.querySelectorAll('dialog[open]')].find((d) => d.matches(':modal'));
  let host = baseHost;
  if (modal) {
    host = modal.querySelector(':scope > .kh-toasts') || baseHost.cloneNode(false);
    if (!host.isConnected) modal.append(host);
    if (modal.classList.contains('kh-cart-dialog')) /** @type {HTMLElement} */ (host).classList.add('kh-toasts--cart');
  }
  document.querySelectorAll('.kh-toasts').forEach((el) => el.replaceChildren());

  const toast = document.createElement('div');
  toast.className = `kh-toast kh-toast--${type}`;
  toast.setAttribute('role', type === 'error' ? 'alert' : 'status');
  const iconId = type === 'error' ? 'kh-tpl-icon-alert' : 'kh-tpl-icon-check';
  const icon = /** @type {HTMLTemplateElement | null} */ (document.getElementById(iconId));
  const body = document.createElement('div');
  body.className = 'kh-toast__body';
  const titleEl = document.createElement('p');
  titleEl.className = 'kh-toast__title';
  titleEl.textContent = title;
  body.append(titleEl);
  if (text) {
    const textEl = document.createElement('p');
    textEl.className = 'kh-toast__text';
    textEl.textContent = text;
    body.append(textEl);
  }
  if (type !== 'undo' && icon) toast.append(icon.content.cloneNode(true));
  toast.append(body);
  if (action && onAction) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'kh-toast__action';
    button.textContent = action;
    button.addEventListener('click', () => {
      toast.remove();
      onAction();
    });
    toast.append(button);
  }
  host.append(toast);
  placeCartToast();

  let remaining = action ? 6000 : 4000;
  let started = Date.now();
  let timer = window.setTimeout(() => toast.remove(), remaining);
  const pause = () => {
    window.clearTimeout(timer);
    remaining -= Date.now() - started;
  };
  const resume = () => {
    started = Date.now();
    timer = window.setTimeout(() => toast.remove(), Math.max(remaining, 1500));
  };
  toast.addEventListener('mouseenter', pause);
  toast.addEventListener('mouseleave', resume);
  toast.addEventListener('focusin', pause);
  toast.addEventListener('focusout', resume);
}
window.khToast = khToast;

/**
 * In the cart the toast sits just above whatever is at the bottom now — the summary with the checkout button, or, once
 * the cart is empty, the «Новинки» carousel — clear of the rows' remove buttons (09.10). kh-cart.js calls it again
 * after each cart redraw, so the toast follows (one short move) instead of hanging over the new layout.
 */
function placeCartToast() {
  const host = /** @type {HTMLElement | null} */ (document.querySelector('.kh-cart-dialog > .kh-toasts--cart'));
  if (!host || !host.firstElementChild) return;
  const dialog = /** @type {HTMLElement} */ (host.parentElement);
  const anchor = dialog.querySelector('.kh-cart__summary') || dialog.querySelector('.kh-cart__picks');
  const bottom = anchor
    ? Math.round(dialog.getBoundingClientRect().bottom - anchor.getBoundingClientRect().top) + 12
    : 24;
  const value = `${bottom}px`;
  if (host.style.getPropertyValue('--kh-toast-bottom') !== value) host.style.setProperty('--kh-toast-bottom', value);
}
window.khPlaceCartToast = placeCartToast;

// Add-to-cart failed → Ink toast with «Ще раз» that repeats the last add.
/** @type {HTMLButtonElement | null} */
let lastAddButton = null;
document.addEventListener(
  'click',
  (event) => {
    const button = /** @type {HTMLElement} */ (event.target).closest?.('button[name="add"]');
    if (button instanceof HTMLButtonElement) lastAddButton = button;
  },
  true
);
document.addEventListener('shopify:cart:error', (event) => {
  // Only failed adds: the cart drawer reports its own (quantity, removal) errors in the row, and the product and
  // certificate pages show a Sand line under the button (.kh-pdp__add-error) — no second message there
  const target = /** @type {HTMLElement} */ (event.target);
  const form = target?.closest?.('product-form-component');
  if (!form || form.querySelector('.kh-pdp__add')) return;
  const button = form.querySelector('button[name="add"]');
  const i18n = khI18n();
  const payload = /** @type {any} */ (event);
  // Shopify's `error` is a short English label («Cart Error»); the readable reason is in detail.description
  const description = payload.detail?.description;
  khToast({
    type: 'error',
    title: i18n.addFailed || 'Не вдалося додати в кошик',
    text: typeof description === 'string' ? description : '',
    action: button === lastAddButton ? i18n.retry || 'Ще раз' : '',
    onAction: () => /** @type {HTMLButtonElement | null} */ (button)?.click(),
  });
});

// No connection → Ink strip under the header while offline.
function updateOnline() {
  const existing = document.querySelector('[data-kh-offline]');
  if (navigator.onLine) {
    existing?.remove();
    return;
  }
  if (existing) return;
  const template = /** @type {HTMLTemplateElement | null} */ (document.getElementById('kh-tpl-offline'));
  const header = document.querySelector('.kh-header-section');
  if (template && header) header.append(template.content.cloneNode(true));
}
window.addEventListener('online', updateOnline);
window.addEventListener('offline', updateOnline);
updateOnline();

// Visitor outside Ukraine (Customer Privacy API getRegion(), e.g. «UA30» / «PL14»): <html data-kh-abroad> hides
// [data-kh-ua-only] — free Nova Poshta delivery is for Ukraine only, delivery abroad is paid by the recipient.
// Unknown region ('') counts as Ukraine. Remembered per session, so kh-head sets it before the first paint.
(() => {
  const shopify = /** @type {any} */ (window).Shopify;
  const apply = (privacy) => {
    const region = String(privacy?.getRegion?.() || '').toUpperCase();
    if (!region) return;
    try {
      sessionStorage.setItem('kh-region', region);
    } catch {}
    const abroad = !region.startsWith('UA');
    if (abroad === document.documentElement.hasAttribute('data-kh-abroad')) return;
    document.documentElement.toggleAttribute('data-kh-abroad', abroad);
    document.dispatchEvent(new CustomEvent('kh:region', { detail: { abroad } }));
  };
  if (shopify?.customerPrivacy) apply(shopify.customerPrivacy);
  else
    shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (!error) apply(shopify.customerPrivacy);
    });
})();

// Cookie consent through Shopify's Customer Privacy API: card after 1 s, no veil; the choice is stored by Shopify.
// Who sees it (theme setting kh_cookie_scope): 'outside_ua' (default, Олександр 07.10) — everyone outside Ukraine
// until they choose (Ukrainian law does not require a banner; Shopify does not ask consent in Ukraine, so tracking is
// allowed there); 'required' — only where Shopify requires consent (EU/EEA/UK); 'everyone'. Two buttons:
// «Прийняти» = analytics + advertising, «Лише необхідні» = neither.
(() => {
  const card = /** @type {HTMLElement | null} */ (document.querySelector('[data-kh-cookie]'));
  if (!card) return;
  const withApi = (callback) => {
    const shopify = /** @type {any} */ (window).Shopify;
    if (shopify?.customerPrivacy) return callback(shopify.customerPrivacy);
    shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (!error && shopify.customerPrivacy) callback(shopify.customerPrivacy);
    });
  };
  const hide = () => (card.hidden = true);
  // No focus move: the card is not modal, and a visitor may already be typing (search)
  const show = () => {
    card.hidden = false;
  };
  // Shopify stores the consent; we also remember the choice for a year so the card does not come back
  // where Shopify does not require consent (it then keeps no record).
  const CHOICE_KEY = 'kh-cookie-choice';
  const YEAR_MS = 365 * 24 * 60 * 60 * 1000;
  const rememberedChoice = () => {
    try {
      const saved = JSON.parse(localStorage.getItem(CHOICE_KEY) || 'null');
      return saved && Date.now() - saved.at < YEAR_MS ? saved.value : null;
    } catch {
      return null;
    }
  };
  const decide = (all) => {
    try {
      localStorage.setItem(CHOICE_KEY, JSON.stringify({ value: all ? 'all' : 'essential', at: Date.now() }));
    } catch {
      /* storage blocked: Shopify's own record still applies */
    }
    hide();
    withApi((privacy) =>
      privacy.setTrackingConsent({ analytics: all, marketing: all, preferences: all, sale_of_data: all }, () => {})
    );
  };

  card.querySelector('[data-kh-cookie-accept]')?.addEventListener('click', () => decide(true));
  card.querySelector('[data-kh-cookie-essential]')?.addEventListener('click', () => decide(false));
  document.addEventListener('click', (event) => {
    if (/** @type {HTMLElement} */ (event.target).closest?.('[data-kh-cookie-open]')) show();
  });
  window.setTimeout(
    () =>
      withApi((privacy) => {
        const consent = privacy.currentVisitorConsent?.() || {};
        const undecided = !consent.analytics && !rememberedChoice(); // Shopify: '' until the visitor chooses
        // getRegion(): country + region code, e.g. «UA30»; empty when Shopify could not tell — then no card
        const region = String(privacy.getRegion?.() || '').toUpperCase();
        const scope = card.dataset.scope || 'outside_ua';
        const wanted =
          scope === 'everyone' || (scope === 'outside_ua' && region !== '' && !region.startsWith('UA'));
        if (privacy.shouldShowBanner?.() || (wanted && undecided)) show();
      }),
    1000
  );
})();

// Fields: validate after leaving the field, not while typing; the message goes away once fixed.
function validateField(input) {
  const field = input.closest('.kh-field');
  if (!field || input.disabled) return;
  const i18n = khI18n();
  const value = input.value.trim();
  let message = '';
  if (input.required && !value) {
    message = i18n.required || 'Заповни це поле.';
  } else if (value && input.type === 'email') {
    if (!value.includes('@')) message = i18n.emailAt || 'Бракує @ — перевір пошту.';
    else if (!input.checkValidity()) message = i18n.emailInvalid || 'Перевір пошту.';
  } else if (value && input.type === 'tel' && value.replace(/\D/g, '').replace(/^380/, '').length !== 9) {
    message = i18n.phone || 'Потрібно 9 цифр після +380.';
  } else if (input.maxLength > 0 && value.length > input.maxLength) {
    message = (i18n.tooLong || '').replace('{{ max }}', String(input.maxLength)).replace('{{ count }}', String(value.length));
  }

  const errorId = `${input.id}-error`;
  let error = document.getElementById(errorId);
  field.classList.toggle('is-error', Boolean(message));
  input.toggleAttribute('aria-invalid', Boolean(message));
  if (!message) {
    error?.remove();
    input.removeAttribute('aria-describedby');
    return;
  }
  if (!error) {
    error = document.createElement('p');
    error.className = 'kh-field__error';
    error.id = errorId;
    const icon = /** @type {HTMLTemplateElement | null} */ (document.getElementById('kh-tpl-icon-alert'));
    if (icon) error.append(icon.content.cloneNode(true));
    error.append(document.createElement('span'));
    field.append(error);
  }
  const span = error.querySelector('span');
  if (span) span.textContent = message;
  input.setAttribute('aria-describedby', errorId);
}
window.khValidateField = validateField;
document.addEventListener('focusout', (event) => {
  const input = event.target;
  if (input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement) {
    if (input.closest('.kh-field')) validateField(input);
  }
});
document.addEventListener('input', (event) => {
  const input = event.target;
  if ((input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement) && input.closest('.kh-field.is-error')) {
    validateField(input);
  }
});


// Unit under a card title → catalogue filtered by the unit (11.10). The card is one <a>, so the unit is a span with
// data-kh-href: catch its click before the card link (and Horizon's card handlers) in the capture phase on window.
function openUnit(event) {
  const unit = event.target instanceof Element ? event.target.closest('.kh-card__unit[data-kh-href]') : null;
  if (!(unit instanceof HTMLElement)) return;
  if (event.type === 'auxclick' && event.button !== 1) return;
  event.preventDefault();
  event.stopPropagation();
  const href = unit.dataset.khHref;
  if (!href) return;
  if (event.type === 'auxclick' || event.metaKey || event.ctrlKey || event.shiftKey) window.open(href, '_blank', 'noopener');
  else window.location.assign(href);
}
window.addEventListener('click', openUnit, { capture: true });
window.addEventListener('auxclick', openUnit, { capture: true });

// Card → product page (09.10, as Kith / Stüssy): a plain navigation with nothing in between — the browser keeps this
// page on screen until the product page is ready and swaps in one frame. Chrome / Edge / Android have it prerendered
// already (speculation rules in kh-system.liquid).
// Safari has no speculation rules: fetch the product page as soon as the finger lands on a card
const prefetched = new Set();
document.addEventListener(
  'pointerdown',
  (event) => {
    if (HTMLScriptElement.supports?.('speculationrules')) return;
    const link = event.target instanceof Element ? event.target.closest('a.kh-card__link') : null;
    if (!(link instanceof HTMLAnchorElement) || prefetched.has(link.href)) return;
    prefetched.add(link.href);
    const hint = document.createElement('link');
    hint.rel = 'prefetch';
    hint.href = link.href;
    document.head.append(hint);
  },
  { passive: true }
);
